import React, { FC, useEffect } from 'react';
import 'react-image-lightbox/style.css';
import '../../../assets/styles/index.scss';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { TemplatesList } from '../../../enums/TemplatesList';
import DefaultLayout from '../../Templates/DefaultTemplate/Layout';
import Layout from '../../Templates/ModernTemplate/Layout';
import { PagesList } from '../../../enums/PagesList';
import Seo from '../Seo/Seo';
import {graphql, PageProps, useStaticQuery} from 'gatsby';
import { AppData } from '../../../interfaces/professional/Professional';
import { useNotification } from '../Notification/Notify';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../interfaces/RootState';
import { setHasError, setIsFetched } from '../../../store/form/formActions';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import 'react-toastify/dist/ReactToastify.css';
import { ToastContainer } from 'react-toastify';
import CloseButton from '../../../assets/icons/inline/cross-gray.inline.svg';
import {sectorsTranslation} from "../../../utils/translationsUtils";

interface Props {
  template: TemplatesList;
  color: string;
  currentPage: string;
  pageId: PagesList;
  metaTitle: string;
  metaDescription: string;
  windowLocation: PageProps['location'];
}

const PageWrapper: FC<Props> = ({ template, currentPage, color, children, metaTitle, metaDescription, pageId, windowLocation }) => {
  const notification = useNotification();
  const { t } = useTranslation();
  const { hasError, isFetched } = useSelector((state: RootState) => state.form);
  const dispatch = useDispatch();

  useEffect(() => {
    if (hasError) {
      notification.error(t('messages.error'));
      dispatch(setHasError(false));
      return;
    }
    if (isFetched) {
      notification.success(t('messages.success'));
      dispatch(setIsFetched(false));
    }
  }, [dispatch, hasError, isFetched, notification, t]);

  useEffect(() => {
    AOS.init({ delay: 100, once: true, disable: 'phone' });
  }, []);

  useEffect(() => {
    document.documentElement.style.setProperty('--color-main', color);
  }, [color]);

  const professionalData: AppData = useStaticQuery(
    graphql`
      query {
        professional {
          data {
            shortName
            sectors {
              title
            }
            customSectors
            businessInfo {
              email
              phone
              euVatNumber
              officialId
              paymentMethod
            }
            languages
            language
            location {
              placeDescription
              placeId
              name
              longitude
              latitude
            }
            locations {
              placeDescription
              placeId
              name
              longitude
              latitude
            }
            socialNetworks {
              name
              url
            }
          }
        }
      }
    `,
  );

  const { shortName, businessInfo, sectors, customSectors, location, locations, languages, socialNetworks, language } =
    professionalData.professional.data;

  const addresses = locations.map(item => item.placeDescription);
  if (location) {
    addresses.push(location.placeDescription);
  }

  const translatedLanguages = [...languages.filter(item => item !== language), language].map(it => t(`common.languages.${it}`));
  const translatedSectors = sectorsTranslation(sectors, language as "en" | "fr");
  const layout = () => {
    if (template === TemplatesList.MODERN) {
      return (
        <Layout
          currentPage={currentPage}
          pageId={pageId}
          logoText={shortName}
          shortName={shortName}
          email={businessInfo.email}
          phoneNumber={businessInfo.phone}
          sectorsList={translatedSectors.map(item => item.title).concat(customSectors)}
          addresses={addresses}
          euVatNumber={businessInfo.euVatNumber}
          languages={translatedLanguages}
          officialId={businessInfo.officialId}
          socialLinks={socialNetworks}
          template={template}
        >
          {children}
        </Layout>
      );
    }
    return (
      <DefaultLayout
        template={template}
        currentPage={currentPage}
        logoText={shortName}
        shortName={shortName}
        email={businessInfo.email}
        phoneNumber={businessInfo.phone}
        sectorsList={sectors.map(item => item.title).concat(customSectors)}
        addresses={addresses}
        euVatNumber={businessInfo.euVatNumber}
        languages={translatedLanguages}
        officialId={businessInfo.officialId}
        socialLinks={socialNetworks}
      >
        {children}
      </DefaultLayout>
    );
  };

  //TODO demo template panel
  return (
    <>
      <Seo title={metaTitle} description={metaDescription} windowLocation={windowLocation} />
      {layout()}
      <ToastContainer icon={false} hideProgressBar closeButton={() => <CloseButton />} />
    </>
  );
};

export default PageWrapper;
